import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

import DropdownMenu from '../../dropdown-menu/DropdownMenu';
import IconButton from '../../button/IconButton';
import MediaDetailModal from './MediaDetailModal';
import { ImagePreviewProps, MediaMetadata, MenuItem } from '../../../types/media-preview';
import { downloadMediaFile } from '../../../utils/downloadFile';

const ImageContainer = styled.div.attrs({
  className: 'u-col'
})<{
  $mode: 'fixed-width' | 'fixed-height' | 'auto' | 'fill';
  $width?: string;
  $height?: string;
  $maxWidth?: string;
  $maxHeight?: string;
}>`
  position: relative;
  
  /* 制約を適用しつつ、画像のアスペクト比を保持 */
  ${props => {
    switch (props.$mode) {
      case 'fixed-width':
        return `
          width: ${props.$width || '100%'};
          height: auto;
          max-height: ${props.$maxHeight || '100dvh'};
          & > img { height: auto; max-height: ${props.$maxHeight || '100dvh'}; }
        `;
      case 'fixed-height':
        return `
          height: ${props.$height || 'auto'};
          width: auto;
          overflow: hidden;
          border-radius: 6px;
          ${props.$maxWidth ? `max-width: ${props.$maxWidth};` : ''}
          & > img { width: auto; height: 100%; }
        `;
      case 'fill':
        // 親の definite height に揃えて全面に敷く（StyledImage が object-fit で調整）。
        return `
          width: 100%;
          height: 100%;
          max-height: 100%;
        `;
      case 'auto':
      default:
        return `
          width: 100%;
          height: auto;
          ${props.$maxWidth ? `max-width: ${props.$maxWidth};` : ''}
          max-height: ${props.$maxHeight || '100dvh'};
          & > img { height: auto; max-height: ${props.$maxHeight || '100dvh'}; }
        `;
    }
  }}
`;

const StyledImage = styled.img<{
  $objectFit?: 'cover' | 'contain';
}>`
  width: 100%;
  height: 100%;
  object-fit: ${props => props.$objectFit || 'contain'};
  border-radius: 6px;
  display: block;
`;

/** 最外ラッパー。fill 時は親の高さに追従して ImageContainer に definite height を渡す。 */
const Root = styled.div<{ $fill?: boolean }>`
  ${props => (props.$fill ? 'height: 100%;' : '')}
`;

const ControlsOverlay = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  gap: 8px;
  z-index: 2;
`;

const OutsideControlsOverlay = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 4px 0 0;
`;

const StatusBar = styled.div`
  font-size: 8px;
  color: var(--color-light-gray);
  padding: 4px 0;
`;

const ImagePreview: React.FC<ImagePreviewProps> = ({
  src,
  thumbnailSrc = null,
  sizeMode = { mode: 'auto', objectFit: 'contain' },
  showMenu = false,
  showDownloadIcon = false,
  showStatusBar = false,
  onLoaded = null,
  fileName,
  fileSize,
  menuOutside = false,
  onDownload,
  downloadUrl,
}) => {
  const [imageMetadata, setImageMetadata] = useState<MediaMetadata | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const loadedSrcRef = useRef<string | null>(null);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  useEffect(() => {
    const imageSrc = thumbnailSrc || src;
    
    if (!imageSrc) return;

    if (loadedSrcRef.current === imageSrc) {
      onLoadedRef.current?.();
      return;
    }

    const img = new Image();
    img.src = imageSrc;
    img.onload = () => {
      loadedSrcRef.current = imageSrc;
      setImageMetadata({
        width: img.naturalWidth,
        height: img.naturalHeight,
        format: src.split('?')[0].split('.').pop() || 'unknown'
      });
      onLoadedRef.current?.();
    };

    img.onerror = () => {
      console.error('Failed to load image:', imageSrc);
      onLoadedRef.current?.();
    };
  }, [src, thumbnailSrc]);

  const handleDownload = () => {
    if (!downloadMediaFile({ downloadUrl, src, fileName, fallbackName: 'image' })) return;
    onDownload?.();
  };

  const handleDetailClick = () => {
    setShowDetailModal(true);
  };

  const menuItems: MenuItem[] = [
    { label: '詳細情報', onClick: handleDetailClick },
    { label: 'ダウンロード', onClick: handleDownload },
  ];

  return (
    <Root $fill={sizeMode.mode === 'fill'}>
      <ImageContainer
        $mode={sizeMode.mode}
        $width={sizeMode.mode === 'fixed-width' ? sizeMode.width : undefined}
        $height={sizeMode.mode === 'fixed-height' ? sizeMode.height : undefined}
        $maxWidth={sizeMode.maxWidth}
        $maxHeight={sizeMode.maxHeight}
      >
        <StyledImage
          src={thumbnailSrc || src}
          alt=""
          loading="lazy"
          $objectFit={sizeMode.objectFit}
        />
        
        {!menuOutside && (
          <ControlsOverlay onClick={(e) => e.stopPropagation()}>
            {showMenu && (
              <DropdownMenu items={menuItems} />
            )}
            {showDownloadIcon && !showMenu && (
              <IconButton onClick={handleDownload} icon="download" />
            )}
          </ControlsOverlay>
        )}
      </ImageContainer>

      {menuOutside && (
        <OutsideControlsOverlay onClick={(e) => e.stopPropagation()}>
          {showMenu && (
            <DropdownMenu items={menuItems} />
          )}
          {showDownloadIcon && !showMenu && (
            <IconButton onClick={handleDownload} icon="download" />
          )}
        </OutsideControlsOverlay>
      )}

      {showStatusBar && imageMetadata && (
        <StatusBar>
          {`${imageMetadata.width} x ${imageMetadata.height} • ${imageMetadata.format}`}
        </StatusBar>
      )}

      {showDetailModal && (
        <MediaDetailModal
          onClose={() => setShowDetailModal(false)}
          fileName={fileName}
          width={imageMetadata?.width}
          height={imageMetadata?.height}
          fileSize={fileSize}
        />
      )}
    </Root>
  );
};

export default ImagePreview;
